import { useLang } from '@common/context/language-provider';
import { objectToSearchParams } from '@common/utils/object-to-searchparams/object-to-search-params';
import { generatePath, useNavigate, useParams } from 'react-router';

/**
 * Provides utility functions for working with routes and history in a React application.
 *
 * @name useAppRouteHelpers
 * @function
 * @example
 * const { generateRouteWithCurrentParams, pushWithCurrentParams } = useAppRouteHelpers();
 *
 * // Generates a route with the current parameters
 * const route = generateRouteWithCurrentParams("/survey/:slug");
 *
 * // Pushes a route with the current parameters to the history
 * pushWithCurrentParams("/survey/:slug");
 */
export const useAppRouteHelpers = () => {
  const params = useParams<{ surveySlug: string }>();
  const { language: lang } = useLang();
  const navigate = useNavigate();

  const generateAbsoluteUrlWithCurrentParams = (route: string) => {
    const url = new URL(window.location.origin);
    url.pathname = generatePath(route, { ...params, lang });
    return url.toString();
  };

  return {
    generateRouteWithCurrentParams: (route: string, args = {}) =>
      generatePath(route, { ...params, ...args, lang }),
    generateAbsoluteUrlWithCurrentParams,
    pushWithCurrentParams: (route: string, args = {}, search = {}) =>
      navigate({
        pathname: generatePath(route, { ...params, ...args, lang }),
        search: objectToSearchParams(search),
      }),
  };
};